import React, { useEffect, useReducer, useRef, useState } from "react";
import api, { hms, formatSeconds } from "../lib/api";
import { trackEvent, EVENTS } from "../lib/analytics";
import { enqueueBadgeUnlock } from "../components/AchievementUnlockedPopup";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Play, Pause, Square, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Study stopwatch — timestamp-based source of truth.
 *
 * Previously we used `setInterval(() => setSeconds(s => s + 1), 1000)` which
 * is unreliable: Chrome throttles intervals to 1 Hz when the tab is hidden,
 * iOS Safari pauses them entirely when the device sleeps, PWAs suspended in
 * the background lose them altogether, and any page refresh resets the
 * counter. All of those resulted in lost study minutes.
 *
 * The new model (reducer-driven so all transitions are pure functions):
 *   • `startedAt` — epoch ms when the *current* run segment began
 *   • `accumulated` — seconds completed in *previous* run segments (i.e.
 *     before the most recent pause→start cycle)
 *   • `running` — bool
 *
 * Elapsed display = accumulated + (running ? (Date.now() - startedAt)/1000 : 0)
 *
 * The interval that drives `tick` ONLY repaints the UI — it is NEVER the
 * source of truth. If the interval runs at 0.1 Hz in the background, that's
 * fine; the moment the user comes back we read `Date.now()` and the display
 * jumps to the correct value. Same for refresh / PWA cold-start / device
 * sleep: we restore `startedAt` from localStorage and recompute from
 * wall-clock time.
 */
const STORAGE_KEY = "syllox-timer-v2";

const INITIAL = { startedAt: null, accumulated: 0, running: false, subject: "none", clientSessionId: null };

// Generate a URL-safe UUID for the client_session_id. `crypto.randomUUID` is
// available in every browser Syllox supports; fallback (very old browsers /
// insecure contexts) uses Math.random for defence in depth. Not cryptographically
// perfect, but the goal here is uniqueness across saves, not adversarial
// resistance.
function makeClientSessionId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch { /* ignore */ }
  return "csid_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 12);
}

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (typeof v !== "object" || v === null) return null;
    // Coerce defensively. `typeof NaN === "number"` and `typeof Infinity === "number"`,
    // so we additionally require Number.isFinite() and floor `accumulated` to an
    // integer so legacy float values from older builds can't leak into the save
    // payload and trip Pydantic's int validator on the backend.
    const startedAt = Number.isFinite(v.startedAt) ? v.startedAt : null;
    const accumulated = Number.isFinite(v.accumulated) ? Math.max(0, Math.floor(v.accumulated)) : 0;
    return {
      startedAt,
      accumulated,
      running: !!v.running,
      subject: typeof v.subject === "string" ? v.subject : "none",
      clientSessionId: typeof v.clientSessionId === "string" && v.clientSessionId ? v.clientSessionId : null,
    };
  } catch {
    return null;
  }
}

function writeStored(snapshot) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // localStorage may throw in iOS private mode or under quota pressure —
    // ignore. The display still works for the current session; we just lose
    // resilience across refresh / device sleep.
  }
}

function computeElapsed({ accumulated, running, startedAt }) {
  // ALWAYS return a non-negative integer. The backend's StudySessionIn
  // schema rejects fractional ints (Pydantic v2), and prior pause/resume
  // cycles can leave `accumulated` as a float. Floor the final value so
  // every caller — display, save payload, analytics — sees an int.
  const base = accumulated || 0;
  if (running && typeof startedAt === "number") {
    return Math.max(0, Math.floor(base + (Date.now() - startedAt) / 1000));
  }
  return Math.max(0, Math.floor(base));
}

// Pure reducer — all transitions in one place, lint-clean. The "now"
// timestamp is passed via the action payload so the reducer stays pure
// (and unit-testable).
function timerReducer(s, action) {
  switch (action.type) {
    case "HYDRATE":
      return action.payload;
    case "START":
      if (s.running) return s;
      // Assign a client_session_id the FIRST time this run cycle starts
      // (i.e. after a Reset/Save). We reuse the same id across pause →
      // resume so the whole Start→Pause→Resume→Save block is a SINGLE
      // idempotent unit on the backend. The id is cleared on RESET.
      return {
        ...s,
        startedAt: action.now,
        running: true,
        clientSessionId: s.clientSessionId || makeClientSessionId(),
      };
    case "PAUSE": {
      if (!s.running || typeof s.startedAt !== "number") return s;
      // Floor `justRan` so the persisted `accumulated` counter stays an
      // integer-second tally across pause→resume cycles. Without this,
      // floats accumulated and eventually leaked into the save payload,
      // tripping Pydantic v2's int validator on the backend (HTTP 422).
      const justRan = Math.max(0, Math.floor((action.now - s.startedAt) / 1000));
      return { ...s, accumulated: (s.accumulated || 0) + justRan, startedAt: null, running: false };
    }
    case "RESET":
      // Full reset — includes clearing the client_session_id so the next
      // Start generates a fresh idempotency key. Without this, a user who
      // Reset → Started again would have their new session dedupe-collide
      // with the previous save.
      return { ...INITIAL, subject: s.subject };
    case "SET_SUBJECT":
      return { ...s, subject: action.value };
    default:
      return s;
  }
}

export default function Timer() {
  // Hydrate synchronously on first render so a refresh during a running
  // session paints the correct elapsed value immediately — no flicker.
  const [state, dispatch] = useReducer(timerReducer, INITIAL, (init) => {
    if (typeof window === "undefined") return init;
    return readStored() || init;
  });
  const [subjects, setSubjects] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [, forceTick] = useState(0);
  const intervalRef = useRef(null);
  // In-flight lock. Prevents a double-clicked "Stop & save" (or a spam-click
  // during network latency) from firing two POSTs. Combined with the
  // client_session_id idempotency key on the backend this makes duplicate
  // saves impossible — this ref is just a UX guard that also stops us
  // hammering the API.
  const savingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  // Persist on every canonical-state change. We DO NOT write the computed
  // `elapsed` — only the raw inputs to computeElapsed().
  useEffect(() => { writeStored(state); }, [state]);

  // Display-only repaint loop. Runs at 1 s when visible; OS may throttle
  // when hidden, which is fine — display syncs on next visibility event.
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (state.running) {
      intervalRef.current = setInterval(() => forceTick((n) => n + 1), 1000);
    }
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [state.running]);

  // When the tab becomes visible again (returning from background / device
  // wake / PWA resume), reconcile from localStorage (another tab could have
  // started/paused the timer) and force a repaint so wall-clock elapsed snaps in.
  useEffect(() => {
    const reconcile = () => {
      const fresh = readStored();
      if (fresh) dispatch({ type: "HYDRATE", payload: fresh });
      forceTick((n) => n + 1);
    };
    const onVisible = () => { if (!document.hidden) reconcile(); };
    document.addEventListener("visibilitychange", onVisible);
    // `pageshow` fires when the page is restored from the bfcache — iOS
    // Safari uses bfcache aggressively; without this the timer would show
    // a stale value for a full second.
    window.addEventListener("pageshow", reconcile);
    window.addEventListener("focus", reconcile);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", reconcile);
      window.removeEventListener("focus", reconcile);
    };
  }, []);

  // Initial data load (independent of timer hydration above).
  useEffect(() => {
    api.get("/subjects").then((r) => setSubjects(r.data)).catch(() => {});
    refreshSessions();
  }, []);

  const refreshSessions = async () => {
    try {
      const { data } = await api.get("/sessions?limit=15");
      setSessions(data);
    } catch { /* keep prior sessions if request fails */ }
  };

  // ----- Timer actions (thin handlers around dispatch) -----
  const onStart = () => dispatch({ type: "START", now: Date.now() });
  const onPause = () => dispatch({ type: "PAUSE", now: Date.now() });
  const setSubject = (value) => dispatch({ type: "SET_SUBJECT", value });
  const onReset = () => {
    dispatch({ type: "RESET" });
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  };

  const onSave = async () => {
    // Double-click / spam-click guard. Even though the backend is now
    // idempotent on (user_id, client_session_id), we still avoid firing a
    // second POST — it wastes a round-trip and can confuse the UI toast.
    if (savingRef.current) return;
    // Read elapsed from the CURRENT clock at click time — not from the
    // display state — so a long-running timer captures every second up to
    // the moment of the press, even if the UI tick is being throttled.
    // `computeElapsed` always returns a non-negative integer; we floor
    // again here as a belt-and-suspenders guard against any future state
    // shape regression (NaN, Infinity, fractional injection).
    const raw = computeElapsed(state);
    const elapsed = Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : 0;
    if (elapsed <= 0) return;
    savingRef.current = true;
    setIsSaving(true);
    try {
      const { data } = await api.post("/sessions", {
        duration_seconds: elapsed,
        subject_id: state.subject === "none" ? null : state.subject,
        // Idempotency key — assigned at Start, cleared on Reset. Backend
        // uses (user_id, client_session_id) to reject duplicates and
        // return the original session verbatim on retries.
        client_session_id: state.clientSessionId || undefined,
      });
      trackEvent(EVENTS.TIMER_SAVED, {
        duration_seconds: elapsed,
        has_subject: state.subject !== "none",
      });
      // Premium-gated badge unlocks ride back on the session response. We
      // enqueue them for the AchievementUnlockedPopup (mounted in Layout) —
      // never blocks the save toast or reset.
      if (Array.isArray(data?.newly_unlocked) && data.newly_unlocked.length > 0) {
        enqueueBadgeUnlock(data.newly_unlocked);
      }
      // When the backend deduped (returned an existing session), the elapsed
      // number came from the ORIGINAL save — not the current display — so
      // formatting the toast against the returned duration keeps the copy
      // honest ("Saved 05:23 of study" for both first-save and retry paths).
      const savedSeconds = Number.isFinite(data?.duration_seconds) ? data.duration_seconds : elapsed;
      toast.success(`Saved ${formatSeconds(savedSeconds)} of study`);
      onReset();
      refreshSessions();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const code = e?.code;
      console.error("Save session failed:", { status, detail, code, message: e?.message });
      const human = status === 401
        ? "Session expired — please sign in again."
        : (typeof detail === "string" ? detail : null)
          || (status ? `Save failed (${status}). Please try again.` : `Save failed: ${code || e?.message || "unknown error"}`);
      toast.error(human);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const elapsed = computeElapsed(state);

  return (
    <div className="space-y-6" data-testid="timer-root">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-600">Focus mode</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Study stopwatch</h1>
        <p className="text-zinc-600 dark:text-zinc-400">Press start. Lock your phone, browse around, come back — your study time keeps counting.</p>
      </div>

      <div className="relative overflow-hidden rounded-3xl border border-zinc-200 bg-white p-10 text-center dark:border-zinc-800 dark:bg-zinc-900" data-testid="timer-card">
        <div className="mx-auto max-w-xs">
          <Select value={state.subject} onValueChange={setSubject}>
            <SelectTrigger data-testid="timer-subject-select"><SelectValue placeholder="Choose a subject (optional)"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No subject</SelectItem>
              {subjects.map((s) => (
                <SelectItem key={s.subject_id} value={s.subject_id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="my-12 font-mono text-6xl font-bold tabular-nums tracking-tight sm:text-8xl" data-testid="timer-display">
          {hms(elapsed)}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3">
          {!state.running ? (
            <Button size="lg" onClick={onStart} className="bg-emerald-500 hover:bg-emerald-600 text-white px-8" data-testid="timer-start-btn">
              <Play size={18} className="mr-1.5"/> Start
            </Button>
          ) : (
            <Button size="lg" onClick={onPause} variant="outline" data-testid="timer-pause-btn">
              <Pause size={18} className="mr-1.5"/> Pause
            </Button>
          )}
          <Button size="lg" onClick={onSave} variant="default" disabled={elapsed === 0 || isSaving} data-testid="timer-save-btn">
            <Square size={18} className="mr-1.5"/> {isSaving ? "Saving…" : "Stop & save"}
          </Button>
          <Button size="lg" onClick={onReset} variant="ghost" data-testid="timer-reset-btn">
            <RotateCcw size={18} className="mr-1.5"/> Reset
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900" data-testid="sessions-history-card">
        <h3 className="font-heading text-lg font-semibold">Session history</h3>
        <p className="text-xs text-zinc-500">Your last sessions</p>
        <div className="mt-4 divide-y divide-zinc-100 dark:divide-zinc-800">
          {sessions.length === 0 ? (
            <div className="py-6 text-center text-sm text-zinc-500">No sessions yet. Start your first one above.</div>
          ) : sessions.map((s) => {
            const subjectName = subjects.find((x) => x.subject_id === s.subject_id)?.name || "—";
            return (
              <div key={s.session_id} className="flex items-center justify-between py-3">
                <div>
                  <div className="text-sm font-medium">{formatSeconds(s.duration_seconds)}</div>
                  <div className="text-xs text-zinc-500">{subjectName} · {new Date(s.created_at).toLocaleString()}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
